/**
 * Coach-marks contextuales de /conversation. Monta el motor de coach-marks
 * (spotlight + popover) sobre el contenedor de la pantalla y dispara cada uno
 * en su momento "just-in-time":
 *
 *   conv_mic / conv_text — al primer turno del usuario. En modo voz señala el
 *                          mic del composer compartido (`.vc-mic`); en modo
 *                          sólo-texto, el campo del composer (`.vc-field`).
 *   conv_corr            — cuando aparece el PRIMER `.err-frag` (error marcado).
 *   conv_inspire         — turno del usuario + ~6s sin grabar (se traba). Gracia:
 *                          si abre Inspirar antes, se da por visto y no aparece.
 *                          «Turno» es `isUserTurn` — lo mismo que habilita la
 *                          lamparita: con la apertura todavía sonando, el mark
 *                          señalaba una lamparita desactivada (#550).
 *   game_harder          — translate-it: el desafío nuevo llegó tras una racha de
 *                          «¡Perfecto!» (`gameArena.harderHint`). Señala «Más
 *                          difícil» del pill del juego: el nivel de las frases
 *                          nunca sube solo, sube cuando el alumno lo toca (dueño,
 *                          2026-10-21). NO se persiste: vale una vez por nivel
 *                          por partida (al salir del juego se olvida).
 *
 * Cada mark del catálogo (`conv_*`) se muestra UNA vez por usuario (persistido en backend, no
 * localStorage) y es INDEPENDIENTE: ninguno silencia a otro — la idea es que
 * todo usuario reciba cada tip una vez, en su contexto. Cierre con un solo
 * botón «Entendido» (o tap afuera / Esc); cualquier cierre lo marca visto.
 *
 * Robustez: NO se dispara con un overlay abierto (Settings / Inspire / Seed); si
 * se abre uno con un mark visible, se OCULTA sin marcarlo visto (reaparece al
 * cerrar). Sólo en conversación ESTÁNDAR (no examen/escenario/programa/A0).
 *
 * El composer (`.vc`: mic, campo, lamparita) queda TOCABLE bajo cualquier mark
 * (`passThrough`): el toque actúa y cierra el mark como visto. El mic y la
 * lamparita (si está habilitada) además se ven iluminados (`lit`). Empezar a grabar
 * también lo cierra, y no se dispara uno mientras se graba. Antes el backdrop se comía el toque del mic: el aviso
 * se cerraba al soltar y lo dicho no llegaba a ningún lado (encargo 114).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { getCoachmarksSeen, markCoachmarkSeen } from '../../lib/englishApi.js';
import { useShipAskOpen } from '../../screens/home/ship/shipAskOpen.js';
import type { UseConversationController } from '../../screens/conversation/useConversationController.js';
import { Coachmark, type CustomCoachmarkContent } from './coachmark-engine.js';
import type { CoachmarkId } from './coachmark-content.js';
import './coachmark.css';

interface ConversationCoachmarksProps {
  /** Contenedor raíz de la pantalla (`.app`), donde se monta la capa. */
  containerRef: RefObject<HTMLDivElement | null>;
  ctrl: UseConversationController;
}

const MIC_DELAY_MS = 380;
const INSPIRE_STALL_MS = 6000;
/** Padding extra del spotlight redondo del mic (el botón es chico, ~48px). */
const MIC_SPOT_PAD = 16;
/** Respiro entre el desafío nuevo y la sugerencia de subir (que se lea la frase primero). */
const HARDER_HINT_DELAY_MS = 900;

const ICON_UP =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19 V 5"/><path d="M6 11 L 12 5 L 18 11"/></svg>';

/** Copy del mark de «Más difícil» (ES, voseo). */
export const GAME_HARDER_MARK: CustomCoachmarkContent = {
  accent: 'lime',
  icon: ICON_UP,
  eyebrow: 'MÁS DIFÍCIL',
  title: 'Parece que este nivel te queda fácil',
  body: 'Venís acertando todas. Si querés frases más difíciles, tocá «Más difícil».',
  round: false,
  side: 'below',
};

/** Lo que puede estar abierto: un mark del catálogo o la sugerencia del juego. */
type ActiveMark = CoachmarkId | 'game_harder';

/** El tour SÓLO va en conversación libre/estándar — no en examen/escenario/programa/A0. */
function isStandardConversation(ctrl: UseConversationController): boolean {
  return !ctrl.examMode && !ctrl.scenarioMission && !ctrl.a0Mission && !ctrl.planLabel;
}

export function ConversationCoachmarks({ containerRef, ctrl }: ConversationCoachmarksProps) {
  const {
    state,
    recording,
    messages,
    inspire,
    settingsOpen,
    seedMenuOpen,
    isVoiceInput,
    isUserTurn,
    gameArena,
  } = ctrl;
  const harderHint = gameArena.harderHint;
  const gameSeed = gameArena.seed;
  const inspireOpen = inspire.open;
  const enabled = isStandardConversation(ctrl);
  // Cualquier overlay/sheet abierto bloquea los marks (y un mark visible se oculta). También la
  // pregunta de «Armá tu nave» («¿Seguís o volvés a la nave?»).
  const shipAskOpen = useShipAskOpen();
  const overlayOpen = settingsOpen || seedMenuOpen || inspireOpen || shipAskOpen;
  const userTurns = useMemo(
    () => messages.filter((m) => m.who === 'user').length,
    [messages],
  );

  const engineRef = useRef<Coachmark | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  const activeRef = useRef<ActiveMark | null>(null);
  // Niveles en los que ya se sugirió «Más difícil» en ESTA partida.
  const hintedBandsRef = useRef<Set<string>>(new Set());
  const [seenLoaded, setSeenLoaded] = useState(false);

  // Refs espejo de valores que leen los callbacks/observers sin re-crearse.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const overlayOpenRef = useRef(overlayOpen);
  overlayOpenRef.current = overlayOpen;
  const recordingRef = useRef(recording);
  recordingRef.current = recording;
  const seenLoadedRef = useRef(false);
  seenLoadedRef.current = seenLoaded;

  const persistSeen = useCallback((id: CoachmarkId): void => {
    if (seenRef.current.has(id)) return;
    seenRef.current.add(id);
    void markCoachmarkSeen(id).catch((err: unknown) => {
      console.warn('[coachmark] no se pudo persistir el mark visto', err);
    });
  }, []);

  // Cierre por acción del usuario (Entendido / tap afuera / Esc) → marca visto.
  const endMark = useCallback(
    (id: CoachmarkId): void => {
      activeRef.current = null;
      persistSeen(id);
    },
    [persistSeen],
  );

  const fire = useCallback(
    (id: CoachmarkId, anchor: HTMLElement, extra?: { round?: boolean; pad?: number }): void => {
      const engine = engineRef.current;
      if (!engine || !seenLoadedRef.current) return;
      if (overlayOpenRef.current || recordingRef.current) return;
      if (activeRef.current || seenRef.current.has(id)) return;
      activeRef.current = id;
      const composer = containerRef.current?.querySelector<HTMLElement>('.vc');
      // La barra de «Armá tu nave» también: su «Volver a la nave» aparece en medio de la charla y
      // un aviso abierto se comía el toque (ronda de la noche del 19/10, #546).
      const shipBar = containerRef.current?.querySelector<HTMLElement>('.sp-wrap');
      engine.show({
        id,
        anchor,
        ...(extra ?? {}),
        passThrough: [composer, shipBar].filter((el): el is HTMLElement => el != null),
        // Mic y lamparita se ven iluminados: se pueden tocar (Emmanuel, 19/10).
        lit: composer ? [...composer.querySelectorAll<HTMLElement>('.vc-mic, .inspire-btn')] : [],
        onEnd: () => endMark(id),
      });
    },
    [endMark, containerRef],
  );

  // ─── Ciclo de vida: motor + set de marks vistos ──────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }
    const engine = new Coachmark(container);
    engineRef.current = engine;

    let cancelled = false;
    void getCoachmarksSeen()
      .then((seen) => {
        if (cancelled) return;
        seenRef.current = new Set(seen);
        setSeenLoaded(true);
      })
      .catch((err: unknown) => {
        // Fail-safe: si no podemos leer el set, NO mostramos nada esta sesión.
        console.warn('[coachmark] no se pudo leer el set de marks vistos', err);
      });

    return () => {
      cancelled = true;
      engine.destroy();
      engineRef.current = null;
    };
  }, [containerRef]);

  // Un overlay que se abre sobre un mark visible lo OCULTA sin marcarlo visto
  // (reaparece cuando el overlay cierra y la condición sigue valiendo).
  useEffect(() => {
    if (overlayOpen && activeRef.current) {
      engineRef.current?.hide();
      activeRef.current = null;
    }
  }, [overlayOpen]);

  // Grabar con un mark abierto lo cierra como visto: el alumno ya está hablando
  // (con el mic tocable bajo el mark, el toque llega al hold, no al backdrop).
  useEffect(() => {
    if (recording && activeRef.current) engineRef.current?.dismiss();
  }, [recording]);

  // Gracia: si el user descubre Inspirar por su cuenta, damos el tip por visto.
  useEffect(() => {
    if (seenLoaded && inspireOpen) persistSeen('conv_inspire');
  }, [seenLoaded, inspireOpen, persistSeen]);

  // ─── Disparador PRIMER TURNO: mic (voz) o teclado (texto) ────────────────
  useEffect(() => {
    if (!seenLoaded || !enabled || overlayOpen) return;
    if (state !== 'listening' || userTurns !== 0) return;
    const container = containerRef.current;
    if (!container) return;

    if (isVoiceInput) {
      if (seenRef.current.has('conv_mic') || activeRef.current) return;
      // Ancla el botón de hold del composer compartido (`.vc-mic`, círculo de
      // ~48px → spotlight redondo; speak nunca usa la variante pill --wide).
      const micBtn = container.querySelector<HTMLElement>('.vc-mic');
      if (!micBtn) return;
      const t = window.setTimeout(
        () => fire('conv_mic', micBtn, { round: true, pad: MIC_SPOT_PAD }),
        MIC_DELAY_MS,
      );
      return () => window.clearTimeout(t);
    }

    // Modo sólo-texto: señala el campo del composer.
    if (seenRef.current.has('conv_text') || activeRef.current) return;
    const compose = container.querySelector<HTMLElement>('.vc-field');
    if (!compose) return;
    const t = window.setTimeout(() => fire('conv_text', compose), MIC_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [seenLoaded, enabled, overlayOpen, state, userTurns, isVoiceInput, fire, containerRef]);

  // ─── Disparador CORRECCIONES: aparece el primer `.err-frag` ───────────────
  // Re-evalúa al cambiar overlay/enabled (re-corre tryCorr en el setup), así un
  // error que apareció con un overlay abierto se muestra al cerrarlo.
  useEffect(() => {
    if (!seenLoaded) return;
    const container = containerRef.current;
    if (!container) return;
    const tryCorr = (): void => {
      if (!enabledRef.current || overlayOpenRef.current) return;
      if (seenRef.current.has('conv_corr') || activeRef.current) return;
      const anchor = container.querySelector<HTMLElement>('.err-frag');
      if (anchor) fire('conv_corr', anchor);
    };
    tryCorr();
    const obs = new MutationObserver(() => tryCorr());
    obs.observe(container, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [seenLoaded, enabled, overlayOpen, fire, containerRef]);

  // ─── Disparador INSPIRAR: turno del usuario + ~6s sin grabar ──────────────
  useEffect(() => {
    if (!seenLoaded || !enabled || overlayOpen) return;
    if (seenRef.current.has('conv_inspire') || activeRef.current) return;
    if (!isUserTurn || recording) return;
    const t = window.setTimeout(() => {
      const anchor = containerRef.current?.querySelector<HTMLButtonElement>('.inspire-btn');
      if (anchor && !anchor.disabled) fire('conv_inspire', anchor);
    }, INSPIRE_STALL_MS);
    return () => window.clearTimeout(t);
  }, [seenLoaded, enabled, overlayOpen, isUserTurn, recording, fire, containerRef]);

  // ─── Disparador MÁS DIFÍCIL (translate-it): racha de «¡Perfecto!» ────────
  useEffect(() => {
    if (gameSeed === null) hintedBandsRef.current.clear();
  }, [gameSeed]);

  useEffect(() => {
    if (!harderHint || !enabled || overlayOpen || recording) return;
    const { band } = harderHint;
    if (activeRef.current || hintedBandsRef.current.has(band)) return;
    const t = window.setTimeout(() => {
      const engine = engineRef.current;
      const container = containerRef.current;
      if (!engine || !container) return;
      if (activeRef.current || overlayOpenRef.current || recordingRef.current) return;
      const anchor = container.querySelector<HTMLButtonElement>('.game-pill__harder');
      if (!anchor || anchor.disabled) return;
      activeRef.current = 'game_harder';
      const composer = container.querySelector<HTMLElement>('.vc');
      engine.show({
        content: GAME_HARDER_MARK,
        anchor,
        // El mic sigue tocable: puede seguir jugando sin cerrar el aviso.
        passThrough: composer ? [composer] : [],
        lit: composer ? [...composer.querySelectorAll<HTMLElement>('.vc-mic')] : [],
        // Cerrado de cualquier forma (Entendido, tocar «Más difícil», grabar):
        // no vuelve en este nivel. Un overlay que lo oculta no pasa por acá.
        onEnd: () => {
          activeRef.current = null;
          hintedBandsRef.current.add(band);
        },
      });
    }, HARDER_HINT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [harderHint, enabled, overlayOpen, recording, containerRef]);

  return null;
}
